import type { ShellDockGroup, ShellPoint, ShellSize } from "../../host/ports.ts";

/** A window as docking reads it; each function returns the caller's own records. */
export type DockableWindow = {
  id: string;
  name: string;
  position: ShellPoint;
  size: ShellSize;
  mobilePosition?: ShellPoint;
  mobileSize?: ShellSize;
  dockGroup?: ShellDockGroup;
};

export const DEFAULT_COMPONENT_DOCK_GAP = 12;

type Geometry = {
  position: { x: number; y: number };
  size: { width: number; height: number };
};

const geometryFor = (
  component: DockableWindow,
  isMobileLayout: boolean,
): Geometry => ({
  position:
    isMobileLayout && component.mobilePosition
      ? component.mobilePosition
      : component.position,
  size:
    isMobileLayout && component.mobileSize
      ? component.mobileSize
      : component.size,
});

const withPosition = <T extends DockableWindow>(
  component: T,
  position: Geometry["position"],
  isMobileLayout: boolean,
): T =>
  isMobileLayout
    ? { ...component, mobilePosition: position }
    : { ...component, position };

const withSize = <T extends DockableWindow>(
  component: T,
  size: Geometry["size"],
  isMobileLayout: boolean,
): T =>
  isMobileLayout
    ? { ...component, mobileSize: size }
    : { ...component, size };

const cleanGap = (gap?: number) =>
  Number.isFinite(gap)
    ? Math.max(6, Math.floor(gap!))
    : DEFAULT_COMPONENT_DOCK_GAP;

const dockMetadata = (
  id: string,
  peerId: string,
  role: ShellDockGroup["role"],
  edge: ShellDockGroup["edge"],
  gap: number,
  align: ShellDockGroup["align"],
): ShellDockGroup => ({ id, peerId, role, edge, gap, align });

export function getDockGroupMembers<T extends DockableWindow>(
  components: readonly T[],
  componentId: string,
) {
  const component = components.find(
    (candidate) =>
      candidate.id === componentId || candidate.name === componentId,
  );
  if (!component) return [];
  if (!component.dockGroup) return [component];
  const members = components.filter(
    (candidate) => candidate.dockGroup?.id === component.dockGroup?.id,
  );
  return members.length >= 2 ? members : [component];
}

const alignContextToAuthoritative = <T extends DockableWindow>(
  context: T,
  authoritative: T,
  isMobileLayout: boolean,
  dimensions?: ShellSize | null,
) => {
  const contextGeometry = geometryFor(context, isMobileLayout);
  const authoritativeGeometry = geometryFor(authoritative, isMobileLayout);
  const metadata = context.dockGroup!;
  const alignedY =
    metadata.align === "center"
      ? authoritativeGeometry.position.y +
        (authoritativeGeometry.size.height - contextGeometry.size.height) / 2
      : authoritativeGeometry.position.y;
  let authoritativePosition = { ...authoritativeGeometry.position };

  if (dimensions) {
    const minimumAuthoritativeX =
      contextGeometry.size.width + metadata.gap;
    const maximumAuthoritativeX = Math.max(
      minimumAuthoritativeX,
      dimensions.width - authoritativeGeometry.size.width,
    );
    authoritativePosition.x = Math.min(
      maximumAuthoritativeX,
      Math.max(minimumAuthoritativeX, authoritativePosition.x),
    );
  }
  const contextPosition = {
    x:
      authoritativePosition.x -
      contextGeometry.size.width -
      metadata.gap,
    y: alignedY,
  };

  return {
    context: withPosition(
      context,
      {
        x: Math.floor(contextPosition.x),
        y: Math.floor(contextPosition.y),
      },
      isMobileLayout,
    ),
    authoritative: withPosition(
      authoritative,
      {
        x: Math.floor(authoritativePosition.x),
        y: Math.floor(authoritativePosition.y),
      },
      isMobileLayout,
    ),
  };
};

export function dockComponentPair<T extends DockableWindow>(
  components: T[],
  contextComponentId: string,
  authoritativeComponentId: string,
  options: {
    id?: string;
    gap?: number;
    align?: ShellDockGroup["align"];
    dimensions?: ShellSize | null;
    isMobileLayout?: boolean;
  } = {},
) {
  const context = components.find(
    (component) => component.id === contextComponentId,
  );
  const authoritative = components.find(
    (component) => component.id === authoritativeComponentId,
  );
  if (!context || !authoritative || context.id === authoritative.id) {
    return null;
  }

  const gap = cleanGap(options.gap);
  const align = options.align ?? "start";
  const groupId =
    options.id ??
    `dock:${[context.id, authoritative.id].sort().join(":")}`;
  const priorGroupIds = new Set(
    [context.dockGroup?.id, authoritative.dockGroup?.id].filter(
      (id): id is string => Boolean(id),
    ),
  );
  const detachedComponents = components.map((component) =>
    component.dockGroup && priorGroupIds.has(component.dockGroup.id)
      ? { ...component, dockGroup: undefined }
      : component,
  );
  const detachedContext = detachedComponents.find(
    (component) => component.id === context.id,
  )!;
  const detachedAuthoritative = detachedComponents.find(
    (component) => component.id === authoritative.id,
  )!;
  const contextWithDock = {
    ...detachedContext,
    dockGroup: dockMetadata(
      groupId,
      authoritative.id,
      "context",
      "left",
      gap,
      align,
    ),
  };
  const authoritativeWithDock = {
    ...detachedAuthoritative,
    dockGroup: dockMetadata(
      groupId,
      context.id,
      "authoritative",
      "right",
      gap,
      align,
    ),
  };
  const aligned = alignContextToAuthoritative(
    contextWithDock,
    authoritativeWithDock,
    options.isMobileLayout ?? false,
    options.dimensions,
  );

  return detachedComponents.map((component) => {
    if (component.id === context.id) return aligned.context;
    if (component.id === authoritative.id) return aligned.authoritative;
    return component;
  });
}

export function moveDockGroup<T extends DockableWindow>(
  components: T[],
  componentId: string,
  newPosition: { x: number; y: number },
  isMobileLayout = false,
) {
  const movedComponent = components.find(
    (component) =>
      component.id === componentId || component.name === componentId,
  );
  if (!movedComponent) return components;
  const members = getDockGroupMembers(components, movedComponent.id);
  const memberIds = new Set(members.map((member) => member.id));
  const oldPosition = geometryFor(movedComponent, isMobileLayout).position;
  const delta = {
    x: newPosition.x - oldPosition.x,
    y: newPosition.y - oldPosition.y,
  };

  return components.map((component) => {
    if (!memberIds.has(component.id)) return component;
    const position = geometryFor(component, isMobileLayout).position;
    return withPosition(
      component,
      {
        x: Math.floor(position.x + delta.x),
        y: Math.floor(position.y + delta.y),
      },
      isMobileLayout,
    );
  });
}

export function resizeDockGroup<T extends DockableWindow>(
  components: T[],
  componentId: string,
  newSize: { width: number; height: number },
  isMobileLayout = false,
) {
  const resized = components.find(
    (component) =>
      component.id === componentId || component.name === componentId,
  );
  if (!resized) return components;
  const resizedComponents = components.map((component) =>
    component.id === resized.id
      ? withSize(component, { ...newSize }, isMobileLayout)
      : component,
  );
  if (!resized.dockGroup) return resizedComponents;

  const context = resizedComponents.find(
    (component) =>
      component.dockGroup?.id === resized.dockGroup?.id &&
      component.dockGroup?.role === "context",
  );
  const authoritative = resizedComponents.find(
    (component) =>
      component.dockGroup?.id === resized.dockGroup?.id &&
      component.dockGroup?.role === "authoritative",
  );
  if (!context || !authoritative) return resizedComponents;
  const aligned = alignContextToAuthoritative(
    context,
    authoritative,
    isMobileLayout,
  );
  return resizedComponents.map((component) => {
    if (component.id === context.id) return aligned.context;
    if (component.id === authoritative.id) return aligned.authoritative;
    return component;
  });
}

/**
 * Commits a pointer resize as one geometry transaction. Position-changing
 * handles must never publish a frame where the resized member and its docked
 * peer are separated.
 */
export function updateDockGroupGeometry<T extends DockableWindow>(
  components: T[],
  componentId: string,
  geometry: {
    position: { x: number; y: number };
    size: { width: number; height: number };
  },
  isMobileLayout = false,
) {
  const resized = components.find(
    (component) =>
      component.id === componentId || component.name === componentId,
  );
  if (!resized) return components;

  let updated = components.map((component) =>
    component.id === resized.id
      ? withPosition(
          withSize(component, { ...geometry.size }, isMobileLayout),
          { ...geometry.position },
          isMobileLayout,
        )
      : component,
  );
  if (!resized.dockGroup) return updated;

  const context = updated.find(
    (component) =>
      component.dockGroup?.id === resized.dockGroup?.id &&
      component.dockGroup?.role === "context",
  );
  const authoritative = updated.find(
    (component) =>
      component.dockGroup?.id === resized.dockGroup?.id &&
      component.dockGroup?.role === "authoritative",
  );
  if (!context || !authoritative) return updated;

  const aligned = alignContextToAuthoritative(
    context,
    authoritative,
    isMobileLayout,
  );
  updated = updated.map((component) => {
    if (component.id === context.id) return aligned.context;
    if (component.id === authoritative.id) return aligned.authoritative;
    return component;
  });
  return updated;
}

export function undockComponentGroup<T extends DockableWindow>(
  components: T[],
  componentId: string,
) {
  const component = components.find(
    (candidate) =>
      candidate.id === componentId || candidate.name === componentId,
  );
  if (!component?.dockGroup) return null;
  const groupId = component.dockGroup.id;
  return components.map((candidate) => {
    if (candidate.dockGroup?.id !== groupId) return candidate;
    const { dockGroup, ...undocked } = candidate;
    void dockGroup;
    return undocked;
  });
}

export function dockGroupIsAdjacent<T extends DockableWindow>(
  components: readonly T[],
  componentId: string,
  isMobileLayout = false,
) {
  const members = getDockGroupMembers(components, componentId);
  const context = members.find(
    (component) => component.dockGroup?.role === "context",
  );
  const authoritative = members.find(
    (component) => component.dockGroup?.role === "authoritative",
  );
  if (!context?.dockGroup || !authoritative) return false;
  const contextGeometry = geometryFor(context, isMobileLayout);
  const authoritativeGeometry = geometryFor(
    authoritative,
    isMobileLayout,
  );
  const expectedX =
    contextGeometry.position.x +
    contextGeometry.size.width +
    context.dockGroup.gap;
  const expectedY =
    context.dockGroup.align === "center"
      ? contextGeometry.position.y +
        (contextGeometry.size.height - authoritativeGeometry.size.height) / 2
      : contextGeometry.position.y;
  return (
    expectedX === authoritativeGeometry.position.x &&
    Math.abs(expectedY - authoritativeGeometry.position.y) < 1
  );
}
